import React, { createRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from "@progress/kendo-react-dialogs";
import { FloatingActionButton, Button } from "@progress/kendo-react-buttons";
import { Badge, BadgeContainer } from '@progress/kendo-react-indicators';
import { getNotifications, updateAbout } from '../../actions/index.js'
import EditAboutForm from '../../components/EditAboutForm';
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from '@progress/kendo-react-layout';
import { formCloser } from '../../utils/formCloser.js';
import {
  Chart,
  ChartTitle,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartSeriesItemTooltip,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartLegend
} from '@progress/kendo-react-charts';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { DatePicker, Calendar } from '@progress/kendo-react-dateinputs';
import { getAnalytics } from '../../actions/portal/analytics/getAnalytics.js';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { getEmails } from '../../actions/portal/email/getEmails.js';
import { getStoredEmails } from '../../actions/portal/email/getStoredEmails.js';

const seriesLabels = {
  visible: true, // Note that visible defaults to false
  padding: 3,
};

const UserLoginChart = ({ data, title, axes, style }) => {
  return (
    <Card style={{ width: "49.5%" }}>
      <CardBody>
      <Chart >
      <ChartLegend position="bottom" orientation="horizontal" />
      <ChartTitle text="User Logins"/>
        <ChartCategoryAxis>
            <ChartCategoryAxisItem categories={["30 Days", "90 Days", "YTD"]}>
            </ChartCategoryAxisItem>
        </ChartCategoryAxis>
        <ChartSeries>
          <ChartSeriesItem labels={seriesLabels} name="Unique Users" type="column" color={"#215f9a"} data={data.users} >
          </ChartSeriesItem>
          <ChartSeriesItem labels={seriesLabels} name="All Mobile" type="column" color={"#46b1e1"} data={data.mobile} >
          </ChartSeriesItem>
          <ChartSeriesItem labels={seriesLabels} name="iOS" type="column" color={"#595959"} data={data.ios} >
          </ChartSeriesItem>
          <ChartSeriesItem labels={seriesLabels} name="Android" type="column" color={"#d1d1d1"} data={data.android} >
          </ChartSeriesItem>
        </ChartSeries>
    </Chart>
      </CardBody>

    </Card>

  )
}

const EmailAndNotifCountChart = ({ data, title, axes, style }) => {
  return (
    <Card style={{ width: "49.5%" }}>
      <CardBody>
    <Chart>
      <ChartLegend position="bottom" orientation="horizontal" />
      <ChartTitle text="Tracked Communications Sent"/>
        <ChartCategoryAxis>
            <ChartCategoryAxisItem roundToBaseUnit={true} categories={["Q1", "Q2", "Q3", "Q4"]} axisCrossingValues={[0, 100]}>
            </ChartCategoryAxisItem>
        </ChartCategoryAxis>
        <ChartSeries>
          <ChartSeriesItem axis={'Communications'} color="#0b76a0" name="Emails" type="column" data={data.emails} >
          </ChartSeriesItem>
          <ChartSeriesItem labels={seriesLabels} axis={'Recipients'} name="Email Recipients" type="line" data={data.emailsTo} >
          </ChartSeriesItem>
          <ChartSeriesItem axis={'Communications'} name="Notifications" color="#a0dcf8" type="column" data={data.notifications} >
          </ChartSeriesItem>
          <ChartSeriesItem labels={seriesLabels} axis={'Recipients'} name="Notification Recipients" type="line" data={data.notificationsTo} >
          </ChartSeriesItem>
        </ChartSeries>
        <ChartValueAxis>
          <ChartValueAxisItem majorUnit={1} name={'Communications'}/>
          <ChartValueAxisItem majorUnit={1000} name={'Recipients'}/>
        </ChartValueAxis>
    </Chart>
    </CardBody>
    </Card>
  )
}

const EmailStatsChart = ({ data, title, axes, style }) => {
  return (
    <Card style={{ width: "49.5%" }}>
      <CardBody>
    <Chart>
      <ChartLegend position="bottom" orientation="horizontal" />
      <ChartTitle text="Email Statistics"/>
        <ChartCategoryAxis>
            <ChartCategoryAxisItem categories={["30 Days", "90 Days", "YTD"]}>
            </ChartCategoryAxisItem>
        </ChartCategoryAxis>
        <ChartSeries>
          <ChartSeriesItem labels={seriesLabels} name="Delivered" type="column" color="#13501b" data={data.delivered} >
          </ChartSeriesItem>
          <ChartSeriesItem labels={seriesLabels} name="Opened" type="column" color="lightgreen" data={data.opened} >
          </ChartSeriesItem>
          <ChartSeriesItem labels={seriesLabels} name="Dropped" type="column" color="red" data={data.dropped} >
          </ChartSeriesItem>
        </ChartSeries>
    </Chart>
    </CardBody>
    </Card>
  )
}

const DashboardAnalytics = () => {
  const { token, user } = useSelector(state => state.authReducer);
  const { users, agencies } = useSelector(state => state.usersReducer);
  const { groups } = useSelector(state => state.groupsReducer);
  const { analytics } = useSelector(state => state.analyticsReducer);
  const { emails, storedEmails } = useSelector(state => state.emailReducer);
  const { adminNotifications } = useSelector(state => state.adminNotificationReducer);
  const [parsedAnalytics, setParsedAnalytics] = useState({});
  const [parsedEmailsAndNotif, setParsedEmailsAndNotif] = useState({});
  const [emailCounts, setEmailCounts] = useState({});
  const [excelData, setExcelData] = useState({});
  const [yearSelector, setYearSelector] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
  const [groupFilter, setGroupFilter] = useState([]);
  const _exporter = createRef();
  
  const excelExport = () => {
      if (_exporter.current) {
          _exporter.current.save();
      }
  };
  
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAnalytics(token));
    dispatch(getEmails(token));
    dispatch(getStoredEmails(token));
    dispatch(getNotifications(token));
    const yearList = [];
    for (let i = 0; i < 100; i++) {
      if (new Date().getFullYear() - i >= 2025) {
        yearList.push(new Date().getFullYear() - i)
      }
    }
    setYearSelector(yearList)
  }, []);

  useEffect(() => {

    let q1 = [], q2 = [], q3 = [], q4 = [];
    let q1To = 0, q2To = 0, q3To = 0, q4To = 0;
    
    //Gets Email Count from local storage list
    for (const email of structuredClone(storedEmails)) {
      const emailDate = new Date(email.dateSent)

      const groupList = groupFilter.map(g => g.Users.map(u => u.email)).flat();

      if (email.toList && JSON.parse(email.toList) != null) {
        let emailToList = JSON.parse(email.toList);
        let newList = [];
        let groupNames = groups.map((g)=>g.name);
        for (let user of emailToList) {
          if (groupNames.includes(user)) {
            groups.find((g) => g.name == user).Users.forEach((u) => newList.push(u.email));
          } else {
            newList.push(user);
          }
        }
        email.toList = JSON.stringify(newList);
      }
      let groupFilterBool = true;
      let groupFilterLen = 0;
      if (groupFilter && groupFilter.length && email.toList) {
        let toList = JSON.parse(email.toList);
        if (toList) {
          groupFilterLen = toList.filter((toUser) => groupList.includes(toUser)).length;
          groupFilterBool = toList.filter((toUser) => groupList.includes(toUser)).length
        }
      }

      if (groupFilterBool) {
        if (dateWithinQuarter(emailDate, 1)) {
          q1.push(email)
          if (email.toList && JSON.parse(email.toList) != null) {
            q1To += groupFilterLen ? groupFilterLen : JSON.parse(email.toList).length;
          }
        } else if (dateWithinQuarter(emailDate, 2)) {
          q2.push(email)
          if (email.toList && JSON.parse(email.toList) != null) {
            q2To += groupFilterLen ? groupFilterLen : JSON.parse(email.toList).length;
          }
        } else if (dateWithinQuarter(emailDate, 3)) {
          q3.push(email)
          if (email.toList && JSON.parse(email.toList) != null) {
            q3To += groupFilterLen ? groupFilterLen : JSON.parse(email.toList).length;
          }
        } else if (dateWithinQuarter(emailDate, 4)) {
          q4.push(email)
          if (email.toList && JSON.parse(email.toList) != null) {
            q4To += groupFilterLen ? groupFilterLen : JSON.parse(email.toList).length;
          }
        }
      }
    }

    let parsedEmailsAndNotif = {
      emails: [q1.length, q2.length, q3.length, q4.length],
      emailsTo: [q1To, q2To, q3To, q4To],
    }
    

    /* Gets Email Count by parsing sendgrid events
    let emailNameThirty = [];
    let emailNameNinty = [];
    let emailNameAll = [];
    for (const [key, value] of Object.entries(emails)) {
      for (let v of value) {
        let emailDate = new Date(v.event_timestamp);
        if (emailDate >= thirtyDays && !emailNameThirty.includes(key)) {
          emailNameThirty.push(key);
        }
        if (emailDate >= nintyDays && !emailNameNinty.includes(key)) {
          emailNameNinty.push(key);
        }
        if (emailDate >= ytdDay && !emailNameAll.includes(key)) {
          emailNameAll.push(key);
        }
      }
    }
    let parsedEmailsAndNotif = {
      emails: [emailNameThirty.length, emailNameNinty.length, emailNameAll.length],
    }
    */

    q1 = []; q2 = []; q3 = []; q4 = [];
    q1To = 0; q2To = 0; q3To = 0; q4To = 0;

    for (const notif of adminNotifications) {
      const notifDate = new Date(notif.dateSent)

      const groupList = groupFilter.map(g => g.Users.map(u => u.email)).flat();
      let groupFilterBool = true;
      let groupFilterLen = 0;

      if (groupFilter && groupFilter.length && notif.toList) {
        let toList = JSON.parse(notif.toList);
        if (toList) {
          groupFilterLen = toList.filter((toUser) => groupList.includes(toUser)).length
          groupFilterBool = toList.filter((toUser) => groupList.includes(toUser)).length
        }
      }

      if (groupFilterBool) {
        if (dateWithinQuarter(notifDate, 1)) {
          q1.push(notif)
          if (notif.toList && JSON.parse(notif.toList) != null) {
            q1To += groupFilterLen ? groupFilterLen : JSON.parse(notif.toList).length;
          }
        } else if (dateWithinQuarter(notifDate, 2)) {
          q2.push(notif)
          if (notif.toList && JSON.parse(notif.toList) != null) {
            q2To += groupFilterLen ? groupFilterLen : JSON.parse(notif.toList).length;
          }
        } else if (dateWithinQuarter(notifDate, 3)) {
          q3.push(notif)
          if (notif.toList && JSON.parse(notif.toList) != null) {
            q3To += groupFilterLen ? groupFilterLen : JSON.parse(notif.toList).length;
          }
        } else if (dateWithinQuarter(notifDate, 4)) {
          q4.push(notif)
          if (notif.toList && JSON.parse(notif.toList) != null) {
            q4To += groupFilterLen ? groupFilterLen : JSON.parse(notif.toList).length;
          }
        }
      }
    }

    parsedEmailsAndNotif.notifications = [q1.length, q2.length, q3.length, q4.length];
    parsedEmailsAndNotif.notificationsTo = [q1To, q2To, q3To, q4To];

    setParsedEmailsAndNotif(parsedEmailsAndNotif);
  }, [storedEmails, adminNotifications, currentYear, groupFilter])

  const generateExcelData = () => {
     let data = {
      userCount: users.length,
      groupCount: groups.length,
      agencyCount: agencies.length,
      cfrCount: users.filter((u) => u.cfrComplete).length
     };

     if (analytics) {
      data = { ...data, ...analytics};
     }
     
     if (emailCounts) {
      if (emailCounts.delivered && emailCounts.delivered.length) {
        data = { ...data, deliveredThirty: emailCounts.delivered[0], deliveredNinty: emailCounts.delivered[1], deliveredAll: emailCounts.delivered[2] }
      }
      if (emailCounts.opened && emailCounts.opened.length) {
        data = { ...data, openedThirty: emailCounts.opened[0], openedNinty: emailCounts.opened[1], openedAll: emailCounts.opened[2] }
      }
      if (emailCounts.dropped && emailCounts.dropped.length) {
        data = { ...data, droppedThirty: emailCounts.dropped[0], droppedNinty: emailCounts.dropped[1], droppedAll: emailCounts.dropped[2] } 
      }
     }

     if (parsedEmailsAndNotif.notifications && parsedEmailsAndNotif.emails) {
      data = { 
        ...data, 
        notifThirty: parsedEmailsAndNotif.notifications[0], notifNinty: parsedEmailsAndNotif.notifications[1], notifAll: parsedEmailsAndNotif.notifications[2],
        emailThirty: parsedEmailsAndNotif.emails[0], emailNinty: parsedEmailsAndNotif.emails[1], emailAll: parsedEmailsAndNotif.emails[2],
        emailsToThirty: parsedEmailsAndNotif.emailsTo[0], emailsToNinty: parsedEmailsAndNotif.emailsTo[1], emailsToAll: parsedEmailsAndNotif.emailsTo[2],
        notificationsToThirty: parsedEmailsAndNotif.notificationsTo[0], notificationsToNinty: parsedEmailsAndNotif.notificationsTo[1], notificationsToAll: parsedEmailsAndNotif.notificationsTo[2],
      }
     }
     setExcelData({
      ...data,
      usersThirty: data.usersThirty ? data.usersThirty.length : 0,
      usersNinty: data.usersNinty ? data.usersNinty.length : 0,
      usersAll: data.usersAll ? data.usersAll.length : 0,
      mobileThirty: data.mobileThirty ? data.mobileThirty.length : 0,
      mobileNinty: data.mobileNinty ? data.mobileNinty.length : 0,
      mobileAll: data.mobileAll ? data.mobileAll.length : 0,
      iosThirty: data.iosThirty ? data.iosThirty.length : 0,
      iosNinty: data.iosNinty ? data.iosNinty.length : 0,
      iosAll: data.iosAll ? data.iosAll.length : 0,
      androidThirty: data.androidThirty ? data.androidThirty.length : 0,
      androidNinty: data.androidNinty ? data.androidNinty.length : 0,
      androidAll: data.androidAll ? data.androidAll.length : 0,
     });
  }

  const eventParser = (event, trackingObj) => {
    switch(event) {
      case 'open':
        trackingObj.opened += 1;
        break;
      case 'delivered':
        trackingObj.delivered += 1;
        break;          
      case 'deferred':
        trackingObj.deferred += 1;
        break;
      case 'dropped':
        trackingObj.dropped += 1;
        break;
      case 'bounced':
        trackingObj.bounced += 1;
        break;
    }
    return trackingObj
  }

  useEffect(() => {
    const thirtyDays = formDateFromRange(30);
    const nintyDays = formDateFromRange(90);
    const ytdDay = formDateFromRange(0);
    let emailCounts = {
      thirty: {
        opened: 0,
        delivered: 0,
        deferred: 0,
        dropped: 0,
        bounced: 0,
      },
      ninty: {
        opened: 0,
        delivered: 0,
        deferred: 0,
        dropped: 0,
        bounced: 0,
      },
      all: {
        opened: 0,
        delivered: 0,
        deferred: 0,
        dropped: 0,
        bounced: 0,
      },
    }
    for (const [key, value] of Object.entries(emails)) {
      const totalUserList = groupFilter ? groupFilter
        .map(g => g.Users.map(u => u.email)).flat()
        : []
      value.forEach(v => {
          let handleCurrentYear = true;
          let handleUserPresence = true;
          let emailDate = new Date(v.event_timestamp);
          if (currentYear && currentYear != new Date().getFullYear()) {
            handleCurrentYear = emailDate <= new Date(currentYear, 11, 31);
          }
          if (groupFilter && groupFilter.length) {
            handleUserPresence = totalUserList.includes(v.email_address)
          }

          if (emailDate >= thirtyDays && handleCurrentYear && handleUserPresence) {
            emailCounts.thirty = eventParser(v.event, emailCounts.thirty);
          }
          if (emailDate >= nintyDays && handleCurrentYear && handleUserPresence) {
            emailCounts.ninty = eventParser(v.event, emailCounts.ninty);
          }
          if (emailDate >= ytdDay && handleCurrentYear && handleUserPresence) {
            emailCounts.all = eventParser(v.event, emailCounts.all);
          }
      });
    }
    setEmailCounts({
      delivered: [emailCounts.thirty.delivered, emailCounts.ninty.delivered, emailCounts.all.delivered],
      opened: [emailCounts.thirty.opened, emailCounts.ninty.opened, emailCounts.all.opened],
      dropped: [emailCounts.thirty.dropped, emailCounts.ninty.dropped, emailCounts.all.dropped],
    })

  }, [emails, currentYear, groupFilter])

  useEffect(() => {
    let parsedAnalytics = {
      users: [],
      ios: [],
      android: [],
      mobile: [],
    };
    for (const [key, value] of Object.entries(analytics)) {
      let targetList = value;
      if (groupFilter && groupFilter.length) {
        targetList = targetList.filter((u) => {
          let userGroupList = null;
          if (u?.Groups) {
            userGroupList = u.Groups.map(g => g.id);
          } else {
            userGroupList = u.user.Groups.map(g => g.id);
          }
          for (let group of groupFilter) {
            if (userGroupList.includes(group.id)) {
              return true;
            }
          }
          return false;
        })
      }
      if (key.includes("users")) {
        parsedAnalytics.users.push(targetList.length);
      } else if (key.includes("ios")) {
        parsedAnalytics.ios.push(targetList.length);
      } else if (key.includes("android")) {
        parsedAnalytics.android.push(targetList.length);
      } else if (key.includes("mobile")) {
        parsedAnalytics.mobile.push(targetList.length);
      }
    }
    setParsedAnalytics(parsedAnalytics);
  }, [analytics, groupFilter])

  useEffect(() => {
    generateExcelData();
  }, [parsedEmailsAndNotif, parsedAnalytics, emailCounts, currentYear, groupFilter])

  const formDateFromRange = (range) => {
    const firstDayOfYear = new Date(currentYear && currentYear != new Date().getFullYear() ? currentYear : new Date().getFullYear(), 0, 1);
    let dateFromRange = 0;
    if (range) {
      dateFromRange = ((currentYear && currentYear != new Date().getFullYear()  ? new Date(currentYear, 11, 31) : new Date()) - ((range * 24 * 60 * 60 * 1000)))
      if (dateFromRange < firstDayOfYear) {
        dateFromRange = firstDayOfYear;
      }
    }
    return dateFromRange ? dateFromRange : firstDayOfYear;
  }

  const dateWithinQuarter = (date, quarter) => {
    const nextYear = new Date(currentYear && currentYear != new Date().getFullYear() ? currentYear + 1 : new Date().getFullYear() + 1, 0, 1);
    const q1Start = new Date(currentYear && currentYear != new Date().getFullYear() ? currentYear : new Date().getFullYear(), 0, 1);
    const q2Start = new Date(currentYear && currentYear != new Date().getFullYear() ? currentYear : new Date().getFullYear(), 3, 1);
    const q3Start = new Date(currentYear && currentYear != new Date().getFullYear() ? currentYear : new Date().getFullYear(), 6, 1);
    const q4Start = new Date(currentYear && currentYear != new Date().getFullYear() ? currentYear : new Date().getFullYear(), 9, 1);
    switch(quarter){
      case 1:
        if (date <= q2Start && date >= q1Start) {
          return true;
        }
        break;
      case 2:
        if (date <= q3Start && date >= q2Start) {
          return true;
        }
        break;
      case 3:
        if (date <= q4Start && date >= q3Start) {
          return true;
        }
        break;
      case 4:
        if (date <= nextYear && date >= q4Start) {
          return true;
        }
        break;
    }
    return false;
  }

  const onYearChange = (event) => {
    if (event.value) {
      setCurrentYear(event.value);
      dispatch(getAnalytics(token, event.value))
    }
  }

  return (
    <>
      <Card style={styles.card}>
        <CardHeader className='k-hbox' style={styles.cardHeader}>
          <CardTitle style={styles.cardTitle}>
            Analytics
            <div style={{ float: 'right', display: 'flex', gap: '40px' }}>
              <DropDownList onChange={onYearChange} style={{ maxWidth: '300px' }} data={yearSelector} defaultValue={currentYear ? currentYear : new Date().getFullYear()} />
              <MultiSelect
                data={groups}
                value={groupFilter}
                textField="name"
                dataItemKey="id"
                onChange={(event) => {setGroupFilter(event.value)}}
              />
              <Button type="button" style={{ float: 'right' }} onClick={excelExport}>
                Export to Excel
              </Button>
            </div>
            <ExcelExport
              fileName={"Analytics: " + new Date().toLocaleDateString()}
              data={[excelData]}
              ref={_exporter}>
                <ExcelExportColumn field="usersThirty" title="All Users - 30 Day Count" />
                <ExcelExportColumn field="usersNinty" title="All Users - 90 Day Count" />
                <ExcelExportColumn field="usersAll" title="All Users - YTD" />
                <ExcelExportColumn field="mobileThirty" title="All Mobile - 30 Day Count" />
                <ExcelExportColumn field="mobileNinty" title="All Mobile - 90 Day Count" />
                <ExcelExportColumn field="mobileAll" title="All Mobile - YTD" />
                <ExcelExportColumn field="iosThirty" title="All iOS Users - 30 Day Count" />
                <ExcelExportColumn field="iosNinty" title="All iOS Users - 90 Day Count" />
                <ExcelExportColumn field="iosAll" title="All iOS Users - YTD" />
                <ExcelExportColumn field="androidThirty" title="All Android Users - 30 Day Count" />
                <ExcelExportColumn field="androidNinty" title="All Android Users - 90 Day Count" />
                <ExcelExportColumn field="androidAll" title="All Android Users - YTD" />
                <ExcelExportColumn field="emailThirty" title="Email Communications - 30 Day Count" />
                <ExcelExportColumn field="emailNinty" title="Email Communications - 90 Day Count" />
                <ExcelExportColumn field="emailAll" title="Email Communications - YTD" />
                <ExcelExportColumn field="emailsToThirty" title="Email Communications To/BCC Count - 30 Day Count" />
                <ExcelExportColumn field="emailsToNinty" title="Email Communications To/BCC Count - 90 Day Count" />
                <ExcelExportColumn field="emailsToAll" title="Email Communications To/BCC Count - All" />
                <ExcelExportColumn field="notifThirty" title="Notification Communications - 30 Day Count" />
                <ExcelExportColumn field="notifNinty" title="Notification Communications - 90 Day Count" />
                <ExcelExportColumn field="notifAll" title="Notification Communications - YTD" />
                <ExcelExportColumn field="notificationsToThirty" title="Notifications Communications To Count - 30 Day Count" />
                <ExcelExportColumn field="notificationsToNinty" title="Notifications Communications To Count - 90 Day Count" />
                <ExcelExportColumn field="notificationsToAll" title="Notifications Communications To Count - YTD" />
                <ExcelExportColumn field="deliveredThirty" title="Delivered Emails - 30 Day Count" />
                <ExcelExportColumn field="deliveredNinty" title="Delivered Emails - 90 Day Count" />
                <ExcelExportColumn field="deliveredAll" title="Delivered Emails - YTD" />
                <ExcelExportColumn field="openedThirty" title="Opened Emails - 30 Day Count" />
                <ExcelExportColumn field="openedNinty" title="Opened Emails - 90 Day Count" />
                <ExcelExportColumn field="openedAll" title="Opened Emails - YTD" />
                <ExcelExportColumn field="droppedThirty" title="Dropped Emails - 30 Day Count" />
                <ExcelExportColumn field="droppedNinty" title="Dropped Emails - 90 Day Count" />
                <ExcelExportColumn field="droppedAll" title="Dropped Emails - YTD" />
                <ExcelExportColumn field="userCount" title="Total Users" />
                <ExcelExportColumn field="groupCount" title="Total Groups" />
                <ExcelExportColumn field="agencyCount" title="Total Agencies" />
                <ExcelExportColumn field="cfrCount" title="CFR Completed Count" />
            </ExcelExport>
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            <UserLoginChart data={parsedAnalytics}/>
            <EmailAndNotifCountChart data={parsedEmailsAndNotif}/>
            <div style={{ width: "49.5%", display: 'flex', flexWrap: 'wrap', color: 'white' }}>
              <Card style={{ width: "50%" }}>
                <CardHeader style={{ backgroundColor: '#595959', color: 'white'}}>
                  Users
                </CardHeader>
                <CardBody style={{ textAlign: 'center', alignContent: 'center', fontSize: 'xxx-large' }}>
                  {groupFilter && groupFilter.length ? [...new Set(groupFilter.map(g => g.Users).flat())].length : users.length}
                </CardBody>
              </Card>
              <Card style={{ width: "50%" }}>
                <CardHeader style={{ backgroundColor: '#595959', color: 'white'}}>
                  Groups
                </CardHeader>
                <CardBody style={{ textAlign: 'center', alignContent: 'center', fontSize: 'xxx-large' }}>
                  {groupFilter && groupFilter.length ? groupFilter.length : groups.length}
                </CardBody>
              </Card>
              <Card style={{ width: "50%" }}>
                <CardHeader style={{ backgroundColor: '#595959', color: 'white'}}>
                  Agencies
                </CardHeader>
                <CardBody style={{ textAlign: 'center', alignContent: 'center', fontSize: 'xxx-large' }}>
                  {groupFilter && groupFilter.length ? [...new Set(groupFilter.map(g => g.Users.map(u => u.agency)).flat())].length : agencies.length}
                </CardBody>
              </Card>
              <Card style={{ width: "50%" }}>
                <CardHeader style={{ backgroundColor: '#595959', color: 'white'}}>
                  CFR Completed
                </CardHeader>
                <CardBody style={{ textAlign: 'center', alignContent: 'center', fontSize: 'xxx-large' }}>
                  {groupFilter && groupFilter.length ? users.filter((u) => u.cfrComplete).filter(u => {
                    for (let filter of groupFilter) {
                      if (u.Groups.map(g=> g.id).includes(filter.id)) {
                        return true;
                      }
                    }
                    return false;
                    }).length : users.filter((u) => u.cfrComplete).length}
                </CardBody>
              </Card>
            </div>
            <EmailStatsChart data={emailCounts}/>
          </div>

        </CardBody>
      </Card>
    </>
  )
}

const styles = {
  card: {
    minWidth: 800,
    margin: 5,
  },
  cardHeader: {
    flexDirection: 'column',
  },
  cardTitle: {
    paddingTop: 10
  },
  cardSubtitle: {
    maxHeight: 35,
    marginTop: 5
  },
  aboutSection: {
    marginBottom: 20
  },
  aboutSectionTitle: {
    fontWeight: 'bold',
    fontSize: 15,
  },
  aboutSectionContent: {
    paddingTop: 5,
    whiteSpace: 'pre-line',
  },
  termsModalContent: {
    whiteSpace: 'pre-line',
  },
}

export default DashboardAnalytics;